/* =============================================================
   Consent modal — Winona privacy preference center

   A port of the Nuxt <ConsentModal> (MKTG-41480) to the Shopify
   theme: the "manage my settings" path that replaced OneTrust's
   preference center. Transcend's own auto-shown banner still
   handles the first-touch decision in opt-in regimes.

   It exposes two categories only: Essential (always on, not
   toggleable) and a single Optional switch that grants or denies
   every non-essential Transcend purpose together.

   It never tracks anything itself. Every button hands the
   decision to airgap.js; the Segment bundle in
   snippets/tracking-codes.liquid already listens for airgap's
   `consent-change` event, so a choice made here fires exactly the
   same events as one made in Transcend's own UI.

   Opening it from anywhere on the site:
     - window.WinonaConsent.open()
     - window.dispatchEvent(new CustomEvent('winona:consent-modal-open'))
     - a click on any element carrying [data-consent-modal-open]
       (this is what the future global footer link will use)
   ============================================================= */
(function () {
  'use strict';

  var OPEN_EVENT = 'winona:consent-modal-open';
  var ESSENTIAL_PURPOSE = 'Essential';

  /* Used only until airgap reports its real purpose list. */
  var DEFAULT_OPTIONAL_PURPOSES = ['Functional', 'Analytics', 'Advertising', 'SaleOfInfo'];

  /* A purpose whose consent has not been decided yet holds this string rather
     than a boolean; what it resolves to depends on the regime. */
  var AUTO_CONSENT = 'Auto';

  function $(sel, scope) { return (scope || document).querySelector(sel); }
  function $$(sel, scope) { return Array.prototype.slice.call((scope || document).querySelectorAll(sel)); }

  /* airgap hands back Sets for purposes/signals; older builds hand back arrays. */
  function toArray(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value.slice();
    if (typeof value.forEach === 'function') {
      var out = [];
      value.forEach(function (item) { out.push(item); });
      return out;
    }
    return [];
  }

  /* ─────────────────────────────────────────────────────────────────
     airgap.js access
     ───────────────────────────────────────────────────────────────── */

  /* The queue airgap.js drains on boot. Pushing onto it works whether
     airgap has loaded yet or not — and it must not clobber a queue the
     Segment bundle already created. */
  function airgapReady(callback) {
    var airgap = window.airgap;
    if (airgap && typeof airgap.ready === 'function') {
      airgap.ready(callback);
      return;
    }
    if (!airgap) airgap = window.airgap = {};
    if (!Array.isArray(airgap.readyQueue)) airgap.readyQueue = [];
    airgap.readyQueue.push(callback);
  }

  function airgapApi() {
    var airgap = window.airgap;
    return airgap && typeof airgap.getConsent === 'function' ? airgap : null;
  }

  /* Transcend's own banner/modal UI (uiV2.js). Loads after airgap and may
     never load at all, so every call site guards. */
  function transcendUi() {
    var ui = window.transcend;
    return ui && typeof ui.hideConsentManager === 'function' ? ui : null;
  }

  function transcendViewState() {
    var ui = transcendUi();
    if (!ui || typeof ui.getViewState !== 'function') return null;
    try { return ui.getViewState(); } catch (e) { return null; }
  }

  function hideTranscendUi() {
    var ui = transcendUi();
    if (!ui) return;
    try { ui.hideConsentManager(); } catch (e) { /* no-op */ }
  }

  function showTranscendUi() {
    var ui = transcendUi();
    if (!ui || typeof ui.showConsentManager !== 'function') return;
    try { ui.showConsentManager(); } catch (e) { /* no-op */ }
  }

  /* ─────────────────────────────────────────────────────────────────
     Purpose helpers
     ───────────────────────────────────────────────────────────────── */

  /* Every purpose the visitor's regime lets them decide on, minus Essential. */
  function optionalPurposesFrom(purposeTypes, regimePurposes) {
    if (!purposeTypes) return DEFAULT_OPTIONAL_PURPOSES.slice();

    var regime = toArray(regimePurposes);

    return Object.keys(purposeTypes).filter(function (name) {
      if (name === ESSENTIAL_PURPOSE) return false;
      var type = purposeTypes[name];
      if (type && type.configurable === false) return false;
      /* An empty regime list means "no regime restriction", not "nothing". */
      return !regime.length || regime.indexOf(name) !== -1;
    });
  }

  /* Is the visitor currently opted in to optional tracking?

     `airgap.isOptedIn()` is the library's own resolved answer, and it is the
     only reliable one. Reading `getConsent().purposes` and testing for `true`
     gets it wrong twice over on this property:

       - Every purpose here is configured `defaultConsent: "Auto"`, so before
         the visitor decides anything the map holds the *string* `"Auto"`,
         not a boolean.
       - The regime is US_DNSS, which is opt-out: `"Auto"` there resolves to
         granted — tracking runs until the visitor refuses it.

     So an undecided visitor is really opted IN, while a `=== true` test
     reports opted out and the switch renders off.

     The fallback only runs on a build without `isOptedIn`, and resolves
     `"Auto"` the same way: granted unless airgap says we're opted out. */
  function readOptedIn(airgap, optional) {
    if (typeof airgap.isOptedIn === 'function') {
      try { return !!airgap.isOptedIn(); } catch (e) { /* fall through */ }
    }

    var purposes = {};
    try { purposes = airgap.getConsent().purposes || {}; } catch (e) { /* no-op */ }

    var optedOut = false;
    try { optedOut = typeof airgap.isOptedOut === 'function' && !!airgap.isOptedOut(); } catch (e) { /* no-op */ }

    if (!optional.length) return false;
    return optional.every(function (name) {
      var value = purposes[name];
      return value === true || (value === AUTO_CONSENT && !optedOut);
    });
  }

  function optionalPurposesPayload(optional, enabled) {
    var payload = {};
    optional.forEach(function (name) { payload[name] = enabled; });
    return payload;
  }

  /* ─────────────────────────────────────────────────────────────────
     Modal
     ───────────────────────────────────────────────────────────────── */
  function ConsentModal(root) {
    this.root = root;
    this.panel = $('.consent-modal__panel', root);
    this.closeButton = $('[data-consent-close]', root);
    this.switchInput = $('[data-consent-switch]', root);
    this.allowAllButton = $('[data-consent-action="allow"]', root);
    this.actionButtons = $$('[data-consent-action]', root);

    this.isOpen = false;
    this.pending = false;
    this.airgapLoaded = false;
    this.lastFocus = null;
    this.optionalPurposes = DEFAULT_OPTIONAL_PURPOSES.slice();

    /* Whether a decision was written to airgap during this opening (as
       opposed to closing with X / Escape / the overlay). */
    this.decided = false;
    /* Whether Transcend's own UI was on screen when we hid it. */
    this.hidTranscendUi = false;

    this.onKeydown = this.onKeydown.bind(this);

    this.bind();
    this.setBusy(false);
  }

  ConsentModal.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      if (event.target === self.root) self.close();
    });

    $$('[data-consent-close]', this.root).forEach(function (el) {
      el.addEventListener('click', function () { self.close(); });
    });

    $$('[data-consent-group-toggle]', this.root).forEach(function (button) {
      button.addEventListener('click', function () {
        var expanded = button.getAttribute('aria-expanded') === 'true';
        self.setGroupExpanded(button, !expanded);
      });
    });

    if (this.switchInput) {
      this.switchInput.addEventListener('change', function () {
        self.syncAllowAllVisibility();
      });
    }

    /* The native click event is passed straight through: airgap.js only
       accepts consent changes backed by a trusted user interaction. */
    this.actionButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        var action = button.dataset.consentAction;
        self.apply(action, event);
      });
    });
  };

  ConsentModal.prototype.setGroupExpanded = function (button, expanded) {
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var panel = document.getElementById(button.getAttribute('aria-controls'));
    if (panel) panel.hidden = !expanded;
  };

  ConsentModal.prototype.collapseAllGroups = function () {
    var self = this;
    $$('[data-consent-group-toggle]', this.root).forEach(function (button) {
      self.setGroupExpanded(button, false);
    });
  };

  /* ── Busy state ────────────────────────────────────────────────── */
  /* Buttons stay disabled until airgap has loaded: before that there is
     nothing to write the decision to, and silently closing would look
     like the choice was saved. */
  ConsentModal.prototype.setBusy = function (pending) {
    this.pending = pending;
    var busy = this.pending || !this.airgapLoaded;
    this.actionButtons.forEach(function (button) { button.disabled = busy; });
  };

  /* ── Reading state back from airgap ────────────────────────────── */
  ConsentModal.prototype.syncFromAirgap = function () {
    var airgap = airgapApi();
    if (!airgap) {
      this.syncAllowAllVisibility();
      return;
    }

    var purposeTypes = null;
    var regimePurposes = null;

    try { purposeTypes = airgap.getPurposeTypes(); } catch (e) { /* no-op */ }
    try { regimePurposes = airgap.getRegimePurposes(); } catch (e) { /* no-op */ }

    this.optionalPurposes = optionalPurposesFrom(purposeTypes, regimePurposes);

    if (this.switchInput) {
      this.switchInput.checked = readOptedIn(airgap, this.optionalPurposes);
    }
    this.syncAllowAllVisibility();
  };

  /* Only offered while optional cookies are off. On open the switch carries
     the visitor's real opt-in state, so the button is hidden for someone
     already opted in and shown for someone opted out; after that it follows
     the switch, so flipping it off offers the one-click way back. */
  ConsentModal.prototype.syncAllowAllVisibility = function () {
    if (!this.allowAllButton) return;
    this.allowAllButton.hidden = !!(this.switchInput && this.switchInput.checked);
  };

  ConsentModal.prototype.consentIsConfirmed = function () {
    var airgap = airgapApi();
    if (!airgap) return false;
    try {
      var consent = airgap.getConsent();
      return !!(consent && consent.confirmed);
    } catch (e) {
      return false;
    }
  };

  /* ── Writing a decision ────────────────────────────────────────── */
  ConsentModal.prototype.apply = function (action, event) {
    var airgap = airgapApi();
    if (!airgap || this.pending) return;

    this.setBusy(true);

    var self = this;
    var write;

    try {
      if (action === 'allow') {
        write = airgap.optIn(event);
      } else if (action === 'reject') {
        write = airgap.optOut(event);
      } else {
        var enabled = !!(self.switchInput && self.switchInput.checked);
        write = airgap.setConsent(event, optionalPurposesPayload(self.optionalPurposes, enabled));
      }
    } catch (error) {
      write = Promise.reject(error);
    }

    Promise.resolve(write)
      .then(function (result) {
        /* airgap reports a refused change by returning false rather than
           throwing (e.g. an untrusted event), so don't call that a decision. */
        if (result === false) {
          console.warn('[consent] airgap refused the consent update');
          return;
        }
        self.decided = true;
        /* The visitor has just answered the question Transcend's banner
           was asking, so take it down for good. */
        hideTranscendUi();
      })
      .catch(function (error) {
        console.warn('[consent] airgap rejected the consent update', error);
      })
      .then(function () {
        self.setBusy(false);
        self.close();
      });
  };

  /* ── Open / close ──────────────────────────────────────────────── */
  ConsentModal.prototype.open = function (returnFocusTo) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.decided = false;
    this.lastFocus = returnFocusTo || document.activeElement;

    /* Transcend's auto-shown banner renders at the maximum z-index and
       swallows clicks on anything under it — including this modal's switch
       and footer buttons on a first visit in an opt-in regime. */
    this.hidTranscendUi = this.hideTranscendUiIfVisible();

    this.syncFromAirgap();
    this.collapseAllGroups();

    this.root.removeAttribute('inert');
    this.root.setAttribute('aria-hidden', 'false');
    this.root.classList.add('is-open');
    document.documentElement.classList.add('consent-modal-locked');
    window.addEventListener('keydown', this.onKeydown);

    var self = this;
    window.requestAnimationFrame(function () {
      if (self.closeButton) self.closeButton.focus({ preventScroll: true });
    });
  };

  ConsentModal.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.root.classList.remove('is-open');
    this.root.setAttribute('aria-hidden', 'true');
    this.root.setAttribute('inert', '');
    document.documentElement.classList.remove('consent-modal-locked');
    window.removeEventListener('keydown', this.onKeydown);

    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      try { this.lastFocus.focus({ preventScroll: true }); } catch (e) { /* no-op */ }
    }

    this.restoreTranscendPrompt();
  };

  ConsentModal.prototype.hideTranscendUiIfVisible = function () {
    var state = transcendViewState();
    var visible = !!state && state !== 'Closed';
    hideTranscendUi();
    return visible;
  };

  /* Closed without deciding, after we took down a banner that was on screen
     and is still unanswered: bring it back, or the visitor would have no way
     left to answer it on this page view. */
  ConsentModal.prototype.restoreTranscendPrompt = function () {
    if (this.decided || !this.hidTranscendUi) return;
    this.hidTranscendUi = false;
    if (this.consentIsConfirmed()) return;
    showTranscendUi();
  };

  ConsentModal.prototype.onKeydown = function (event) {
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  };

  ConsentModal.prototype.trapFocus = function (event) {
    var focusables = $$([
      'a[href]', 'button:not([disabled])', 'input:not([disabled])',
      'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])'
    ].join(','), this.panel).filter(function (el) {
      return !el.hasAttribute('hidden') && el.offsetParent !== null;
    });
    if (!focusables.length) return;

    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ── airgap wiring ─────────────────────────────────────────────── */
  ConsentModal.prototype.watchAirgap = function () {
    var self = this;

    airgapReady(function () {
      self.airgapLoaded = true;
      self.setBusy(false);

      var airgap = airgapApi();
      if (airgap && typeof airgap.addEventListener === 'function') {
        airgap.addEventListener('consent-change', function () { self.syncFromAirgap(); });
      }

      /* Opened before airgap finished loading: re-read now, so the switch
         isn't stuck on the fallback state until the visitor reopens. */
      if (self.isOpen) self.syncFromAirgap();
    });

    /* uiV2.js loads after airgap and may auto-show its banner *after* this
       modal is already open — straight over the switch and footer buttons.
       Hide it the moment it appears, too. */
    var ui = transcendUi();
    if (ui && typeof ui.addEventListener === 'function') {
      ui.addEventListener('view-state-change', function (event) {
        if (!self.isOpen) return;
        var state = (event && event.detail && event.detail.viewState) || transcendViewState();
        if (state && state !== 'Closed') {
          self.hidTranscendUi = true;
          hideTranscendUi();
        }
      });
    }
  };

  /* ─────────────────────────────────────────────────────────────────
     Boot
     ───────────────────────────────────────────────────────────────── */
  function init() {
    var root = $('[data-consent-modal]');
    if (!root || root._consentModal) return;

    var modal = new ConsentModal(root);
    root._consentModal = modal;
    modal.watchAirgap();

    window.WinonaConsent = {
      open: function (returnFocusTo) { modal.open(returnFocusTo); },
      close: function () { modal.close(); }
    };

    window.addEventListener(OPEN_EVENT, function () { modal.open(); });

    /* Delegated so triggers rendered later (or by a CMS metafield) work. */
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest && event.target.closest('[data-consent-modal-open]');
      if (!trigger) return;
      event.preventDefault();
      modal.open(trigger);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
